// M7.3 — /app/report/print. A browser-printable view of the same
// magazine HTML we ship to DocRaptor. Users who want "print it to my
// office printer" get Ctrl+P here; DocRaptor itself receives the HTML
// via server-side render, not by fetching this route — so this page
// can stay authenticated behind the session cookie.

import { useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { BROWSER_PRINT_CLASS, buildPrintHtml } from "../../shared/report/print-html";
import {
  fetchBootstrap,
  fetchReport,
  type BootstrapResponse,
  type ReportCompare,
  type ReportPayload,
  type ReportRange,
} from "../lib/api";
import { usePageMeta } from "../hooks/usePageMeta";
import { withBase } from "../lib/base-path";

type Status =
  | { kind: "loading" }
  | { kind: "unauthenticated" }
  | { kind: "no-property" }
  | { kind: "error"; message: string }
  | { kind: "ready"; bootstrap: BootstrapResponse; report: ReportPayload };

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const VALID_RANGES: ReadonlyArray<ReportRange> = ["last_7d", "last_28d", "last_90d"];
const VALID_COMPARES: ReadonlyArray<ReportCompare> = ["previous", "year_over_year", "none"];

function isIsoDate(v: string | null): v is string {
  return v !== null && ISO_DATE_RE.test(v);
}
function isRange(v: string | null): v is ReportRange {
  return v !== null && (VALID_RANGES as readonly string[]).includes(v);
}
function isCompare(v: string | null): v is ReportCompare {
  return v !== null && (VALID_COMPARES as readonly string[]).includes(v);
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function default28d(): { startDate: string; endDate: string } {
  const today = new Date();
  const end = new Date(today);
  end.setDate(today.getDate() - 1);
  const start = new Date(end);
  start.setDate(end.getDate() - 27);
  return { startDate: isoDate(start), endDate: isoDate(end) };
}

export function PrintReportPage() {
  usePageMeta({ title: "Print report · Traffic Snapshot" });
  const [params] = useSearchParams();
  // M12.2 — URL is bounds-first (start/end), with a legacy `?range=`
  // fallback so a print link minted before this milestone still resolves.
  const fallback = useMemo(() => default28d(), []);
  const rawStart = params.get("start");
  const rawEnd = params.get("end");
  const rawRange = params.get("range");
  const useBounds = isIsoDate(rawStart) && isIsoDate(rawEnd);
  const startDate = useBounds ? (rawStart as string) : fallback.startDate;
  const endDate = useBounds ? (rawEnd as string) : fallback.endDate;
  const legacyRange: ReportRange | null = !useBounds && isRange(rawRange) ? rawRange : null;
  const compare: ReportCompare = isCompare(params.get("compare"))
    ? (params.get("compare") as ReportCompare)
    : "previous";

  const [status, setStatus] = useState<Status>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const bootstrap = await fetchBootstrap();
        if (cancelled) return;
        if (!bootstrap.authenticated) return setStatus({ kind: "unauthenticated" });
        if (!bootstrap.defaultProperty) return setStatus({ kind: "no-property" });
        const report = await fetchReport(
          legacyRange
            ? {
                propertyId: bootstrap.defaultProperty.propertyId,
                range: legacyRange,
                compare,
              }
            : {
                propertyId: bootstrap.defaultProperty.propertyId,
                startDate,
                endDate,
                compare,
              },
        );
        if (cancelled) return;
        setStatus({ kind: "ready", bootstrap, report });
      } catch (err) {
        if (cancelled) return;
        setStatus({ kind: "error", message: (err as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, legacyRange, compare]);

  if (status.kind === "unauthenticated") {
    return <Navigate to="/app/connect?return_to=%2Fapp%2Freport%2Fprint" replace />;
  }
  if (status.kind === "loading") {
    return (
      <div className="grid min-h-dvh place-items-center bg-white text-neutral-500">
        <p className="font-mono text-xs tracking-wide uppercase">Rendering preview…</p>
      </div>
    );
  }
  if (status.kind === "no-property" || status.kind === "error") {
    return (
      <div className="grid min-h-dvh place-items-center bg-white p-8 text-center">
        <div>
          <p className="font-mono text-xs tracking-wide uppercase text-neutral-500">
            Can't render preview
          </p>
          <p className="mt-2 text-sm text-neutral-700">
            {status.kind === "error"
              ? status.message
              : "Pick a property before opening the print view."}
          </p>
        </div>
      </div>
    );
  }

  const propertyLabel = status.bootstrap.defaultProperty?.propertyName ?? "report";
  const html = buildPrintHtml({
    payload: status.report,
    propertyLabel,
    logoUrl: window.location.origin + withBase("/sked-logo.webp"),
  });

  return (
    <div
      className={BROWSER_PRINT_CLASS}
      aria-label={`Printable Traffic Snapshot for ${propertyLabel}`}
      // dangerouslySetInnerHTML is unavoidable here — we're rendering a
      // full HTML document string produced by the shared print-html
      // builder so the browser Ctrl+P output matches the DocRaptor PDF
      // byte-for-byte. The payload that drives it is strictly typed +
      // comes from our own /api/report.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
